import { Link } from "react-router-dom";
import { lazy, Suspense, type ReactNode, useEffect, useRef, useState } from "react";
import { trackPhoneClick } from "@/lib/tracking";
import { SITE } from "@/lib/site";
const heroVideo = "/videos/hero-banner-v2.mp4";
const heroVideoMobile = "/videos/hero-banner-mobile-v3.mp4";
const heroPoster = "/videos/hero-banner-poster.avif";
const heroPosterMobile = "/videos/hero-banner-mobile-poster.avif";

const Icon = ({ path, className = "h-4 w-4" }: { path: string; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d={path} />
  </svg>
);

const PHONE_PATH = "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.62 2.6a2 2 0 0 1-.45 2.11L8 9.72a16 16 0 0 0 6.28 6.28l1.29-1.28a2 2 0 0 1 2.11-.45c.83.29 1.7.5 2.6.62A2 2 0 0 1 22 16.92Z";
const SHIELD_PATH = "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z";
const AWARD_PATH = "M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM8.5 14 7 22l5-3 5 3-1.5-8";
const STAR_PATH = "m12 2 3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2Z";
const ARROW_PATH = "M5 12h14M13 5l7 7-7 7";
import QuizForm from "@/components/QuizForm";
import teamImage from "@/assets/team.webp";
import { usePageMeta } from "@/hooks/usePageMeta";

const ServiceCardsGrid = lazy(() => import("@/components/site/ServiceCardsGrid"));
const StatsRow = lazy(() => import("@/components/site/StatsRow"));
const FreeEstimateForm = lazy(() => import("@/components/site/FreeEstimateForm"));
const FinalCTA = lazy(() => import("@/components/site/FinalCTA"));
const MiniFAQ = lazy(() => import("@/components/site/MiniFAQ"));
const BeforeAfterGallery = lazy(() => import("@/components/BeforeAfterGallery"));
const ElfsightReviews = lazy(() => import("@/components/site/ElfsightReviews"));
const LocationMap = lazy(() => import("@/components/site/LocationMap"));

const LazyOnView = ({ children, className = "", rootMargin = "700px 0px" }: { children: ReactNode; className?: string; rootMargin?: string }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isMounted) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsMounted(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMounted, rootMargin]);

  return <div ref={ref} className={className}>{isMounted ? children : null}</div>;
};

const Home = () => {
  usePageMeta({
    title: "MD Custom Construction | Southern Maryland Remodelers",
    description:
      "Trusted Southern Maryland remodelers. Kitchens, baths, basements, decks, roofing & new home construction. Free estimate in 30 seconds.",
    canonical: "https://mdcustomconstruction.com/",
  });

  const faqItems = [
    { q: "What areas of Southern Maryland do you serve?", a: "We serve St. Mary's, Calvert, and Charles counties, including Hollywood, Leonardtown, California, Lexington Park, Lusby, Prince Frederick, Waldorf, La Plata, and surrounding communities." },
    { q: "Are you licensed, bonded, and insured?", a: "Yes. MD Custom Construction is fully licensed, bonded, and insured in Maryland. MHIC #110740 and MHBR #8487." },
    { q: "Do you offer free estimates?", a: "Yes. Every estimate is free and no-obligation. Call (240) 587-2207 or use our quick online form and we'll respond within one business day." },
    { q: "Do you offer financing?", a: "Yes. We partner with trusted lenders so you can move forward without delay. Apply on our financing page or ask during your estimate." },
    { q: "How long does a typical remodel take?", a: "Bathrooms usually run 2–4 weeks, kitchens 4–8 weeks, and basements 6–10 weeks. Decks and screen rooms are typically 1–3 weeks. We provide a written schedule before work begins." },
    { q: "Do you build new homes too?", a: "Yes. We are a licensed Maryland Home Builder (MHBR #8487) and build full custom homes alongside our remodeling work." },
  ];

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [selectedHeroPoster, setSelectedHeroPoster] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches ? heroPosterMobile : heroPoster
  );
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const updatePoster = () => setSelectedHeroPoster(media.matches ? heroPosterMobile : heroPoster);
    updatePoster();
    media.addEventListener("change", updatePoster);
    return () => media.removeEventListener("change", updatePoster);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const play = () => {
      v.muted = true;
      v.defaultMuted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    play();
    v.addEventListener("loadeddata", play, { once: true });
    v.addEventListener("canplay", play, { once: true });
    document.addEventListener("visibilitychange", play);
    return () => {
      v.removeEventListener("loadeddata", play);
      v.removeEventListener("canplay", play);
      document.removeEventListener("visibilitychange", play);
    };
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-foreground">
        <div className="absolute inset-0 -z-10 bg-foreground">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={selectedHeroPoster}
            aria-label="MD Custom Construction projects video"
            className="h-full w-full object-cover"
          >
            <source src={heroVideoMobile} type="video/mp4" media="(max-width: 767px)" />
            <source src={heroVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-foreground/45" />
        </div>
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-20 text-primary-foreground md:py-28 lg:grid-cols-[1.2fr_1fr] lg:items-center lg:gap-12">
          <div>
            <p className="mb-4 inline-block rounded-full bg-accent/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-accent">
              Southern Maryland's Trusted Remodelers
            </p>
            <h1 className="max-w-3xl font-heading text-4xl font-extrabold leading-tight md:text-6xl">
              {SITE.tagline}
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-primary-foreground/85">
              {SITE.subTagline}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/apply-now-for-financing/" className="rounded-full border-2 border-primary-foreground/40 bg-primary-foreground/10 px-6 py-3 text-sm font-bold text-primary-foreground backdrop-blur hover:bg-primary-foreground/20">
                Apply Now For Financing
              </Link>
              <a href={SITE.phoneHref} onClick={trackPhoneClick} className="inline-flex items-center gap-2 rounded-full border-2 border-primary-foreground/40 bg-primary-foreground/10 px-6 py-3 text-sm font-bold text-primary-foreground backdrop-blur hover:bg-primary-foreground/20">
                <Icon path={PHONE_PATH} /> Call {SITE.phone}
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold text-primary-foreground/85">
              <span className="flex items-center gap-2"><Icon path={SHIELD_PATH} className="h-4 w-4 text-accent" /> Licensed, Bonded &amp; Insured</span>
              <span className="flex items-center gap-2"><Icon path={AWARD_PATH} className="h-4 w-4 text-accent" /> 5-Year Workmanship Warranty</span>
              <span className="flex items-center gap-2"><Icon path={STAR_PATH} className="h-4 w-4 text-accent" /> 4.9 Star Rating</span>
            </div>
          </div>

          <div className="w-full lg:max-w-md lg:justify-self-end">
            <div className="rounded-2xl bg-background/95 p-3 text-foreground shadow-2xl backdrop-blur md:p-4">
              <div className="mb-2 text-center">
                <h2 className="font-heading text-base font-bold text-foreground md:text-lg">
                  Free Estimate in 30 Seconds
                </h2>
              </div>
              <QuizForm standalone compact />
            </div>
          </div>
        </div>
      </section>

      {/* About preview */}
      <section className="section-padding bg-background">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <img
              src={teamImage}
              alt="MD Custom Construction team in front of the Hollywood, MD showroom"
              loading="lazy"
              className="aspect-[4/5] w-full rounded-2xl object-cover shadow-md"
            />
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">About Us</p>
              <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">About MD Custom Construction</h2>
              <p className="mt-5 text-base text-muted-foreground">
                Our Home Advisor-listed company has a track record of meeting our customers' high expectations, and we back every project with a 5-year workmanship warranty. Contact us today for a free estimate.
              </p>
              <Link to="/about/" aria-label="Learn more about MD Custom Construction" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
                Learn More <Icon path={ARROW_PATH} />
              </Link>
            </div>
          </div>
          <div className="mt-10">
            <Suspense fallback={<div className="min-h-24" />}>
              <StatsRow />
            </Suspense>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-secondary">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 text-center">
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">What We Do</p>
            <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">Services We Provide</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              We offer a full range of home improvement services, from bath remodels to roofing, all backed by years of experience.
            </p>
          </div>
          <Suspense fallback={<div className="min-h-[220px]" />}>
            <ServiceCardsGrid />
          </Suspense>
          <div className="mt-10 text-center">
            <Link to="/home-remodeling-services/" className="inline-flex items-center gap-2 font-semibold text-primary hover:underline">
              View All Services <Icon path={ARROW_PATH} />
            </Link>
          </div>
        </div>
      </section>

        <LazyOnView className="min-h-[520px] bg-background">
          <Suspense fallback={<section className="bg-background py-14" />}>
            <ElfsightReviews />
          </Suspense>
        </LazyOnView>

      {/* Recent Projects */}
      <section className="section-padding bg-background">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10">
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">Our Work</p>
            <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">MD Custom Construction Recent Projects</h2>
            <p className="mt-4 max-w-3xl text-muted-foreground">
              At MD Custom Construction, we go beyond kitchen and bath remodeling in Southern Maryland. We specialize in transforming spaces with our expertise in garage and deck construction. Facing roofing issues? Consider it handled. Need to finish your basement? We'll complete it with meticulous care.
            </p>
          </div>
        </div>
        <LazyOnView className="min-h-[980px] bg-background" rootMargin="900px 0px">
          <Suspense fallback={<div className="section-padding" />}>
            <BeforeAfterGallery />
          </Suspense>
        </LazyOnView>
        <div className="mt-6 text-center">
          <Link to="/gallery/" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
            View All Projects <Icon path={ARROW_PATH} />
          </Link>
        </div>
      </section>

      {/* Free Estimate */}
      <section className="section-padding bg-background">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2 lg:items-start">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">Get Started</p>
            <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">Free Estimate</h2>
            <p className="mt-5 text-muted-foreground">
              For your Free Estimate on any of our general contracting or remodeling services, call <a href={SITE.phoneHref} onClick={trackPhoneClick} className="font-bold text-primary hover:underline">{SITE.phone}</a> today or fill out the form and we will call you back.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-foreground">
              <li>✓ Honest, fixed pricing</li>
              <li>✓ Financing available</li>
              <li>✓ Licensed, bonded &amp; insured</li>
              <li>✓ Backed by a 5-year workmanship warranty</li>
            </ul>
          </div>
          <LazyOnView className="min-h-[420px]" rootMargin="500px 0px">
            <Suspense fallback={<div className="min-h-[420px] rounded-2xl bg-secondary" />}>
              <FreeEstimateForm />
            </Suspense>
          </LazyOnView>
        </div>
      </section>

      {/* FAQ with schema for SEO */}
      <section className="section-padding bg-background">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 text-center">
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">FAQ</p>
            <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
              Southern Maryland Remodeling Questions, Answered
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              Quick answers about our <Link to="/home-remodeling-services/" className="text-primary hover:underline">remodeling services</Link>,
              {" "}
              <Link to="/apply-now-for-financing/" className="text-primary hover:underline">financing</Link>, warranty, and process.
              See more on our <Link to="/about/faq/" className="text-primary hover:underline">full FAQ page</Link>.
            </p>
          </div>
          <LazyOnView className="min-h-[360px]" rootMargin="500px 0px">
            <Suspense fallback={<div className="min-h-[360px]" />}>
              <MiniFAQ
                heading=""
                items={faqItems}
              />
            </Suspense>
          </LazyOnView>
        </div>
      </section>

      <LazyOnView className="min-h-[260px]" rootMargin="500px 0px">
        <Suspense fallback={<div className="min-h-[260px] bg-foreground" />}>
          <FinalCTA />
        </Suspense>
      </LazyOnView>

      <LazyOnView className="min-h-[480px] bg-secondary" rootMargin="600px 0px">
        <Suspense fallback={<div className="min-h-[480px] bg-secondary" />}>
          <LocationMap />
        </Suspense>
      </LazyOnView>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HomeAndConstructionBusiness",
            name: SITE.name,
            telephone: SITE.phone,
            email: SITE.email,
            url: "https://mdcustomconstruction.com/",
            image: "https://mdcustomconstruction.com/og-image.jpg",
            priceRange: "$$",
            aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "153" },
            address: {
              "@type": "PostalAddress",
              streetAddress: SITE.address.street,
              addressLocality: SITE.address.city,
              addressRegion: SITE.address.region,
              postalCode: SITE.address.postal,
              addressCountry: "US",
            },
            areaServed: [
              "Southern Maryland",
              "St. Mary's County, MD",
              "Calvert County, MD",
              "Charles County, MD",
            ],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Home Remodeling Services",
              itemListElement: [
                "Kitchen Remodeling", "Bathroom Remodeling", "Basement Finishing",
                "Deck Construction", "Screen Room", "Garage Construction",
                "Home Improvement", "Roofing", "New Home Construction",
              ].map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s } })),
            },
            openingHours: "Mo-Fr 08:00-16:30",
            sameAs: [],
          }),
        }}
      />
    </>
  );
};

export default Home;